import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import backdrop from '../img/backdrop.png';

function Invest() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contactEmail = 'info@screamify.com';

  return (
    <HelmetProvider>
      <Helmet>
        <title>Partner with Screamify™ | Invest in Independent Horror</title>
        <meta name="description" content="Explore Executive Producer partnership opportunities with Screamify. Help fund original independent horror films and empower new creative voices." />
        <meta name="keywords" content="invest in horror film, independent film investment, horror movie production partner, screamify executive producer, support indie horror" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${backdrop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header />
        <Container component="main" maxWidth="md" sx={{ pt: { xs: 10, md: 12 }, pb: 6, flexGrow: 1 }}>
           <Typography 
              variant={isMobile ? "h4" : "h3"} 
              component="h1" 
              align="center" 
              sx={{ 
                  color: 'white', 
                  fontWeight: 'bold', 
                  textShadow: '1px 1px 3px rgba(0,0,0,0.7)', 
                  mb: 4 
              }}
           >
             Partner with Screamify
          </Typography>
          <Typography 
             variant={isMobile ? "h6" : "h5"} 
             component="h2" 
             align="center" 
             sx={{ 
                 color: 'rgba(255, 255, 255, 0.9)', 
                 fontWeight: '500', 
                 textShadow: '1px 1px 2px rgba(0,0,0,0.5)', 
                 mb: 6 
             }}
          >
             Fuel the Future of Independent Horror
          </Typography>

          <Paper
            elevation={6}
            sx={{
              p: { xs: 3, sm: 5 },
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              Screamify™ isn't just building a streaming platform; we're igniting a movement. We champion the bold, the unconventional, the truly independent voices in horror that are often overlooked by the mainstream machine. We operate on a different model – one built on direct support for creators and a commitment to producing unique, original horror content.
            </Typography>
            
            <Typography variant="h5" component="h3" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#ff4d4d' }}>
              The Executive Producer Opportunity
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              This is more than an investment; it's a partnership. As an Executive Producer partner, you play a crucial role in bringing daring new horror visions to life. You'll be directly involved in empowering emerging filmmakers, fostering creativity, and building a sustainable ecosystem where unique stories can thrive.
            </Typography>

            <Typography variant="h5" component="h3" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#ff4d4d' }}>
              Our Production Vision
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              We are dedicated to creating a powerful slate of original Screamify™ productions. Our roadmap is ambitious:
              <ul>
                <li>Year 1: Produce at least <strong>two</strong> independent feature horror films.</li>
                <li>Year 2: Expand to <strong>five</strong> original features.</li>
                <li>Year 3: Scale production to <strong>ten</strong> features annually.</li>
              </ul>
              Our projects span a range of scopes, from intimate, character-driven nightmares with budgets around $50,000, to larger, more complex productions reaching towards $1 million, allowing us to explore diverse subgenres and support varied creative ambitions.
            </Typography>

            <Typography variant="h5" component="h3" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#ff4d4d' }}>
              Featured Project: Death in Dixieland
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              We are currently seeking key Executive Producer partners for our upcoming Southern Gothic feature film, <strong>Death in Dixieland</strong>. This chilling tale is scheduled for production with a target release in 2026. Join us from the outset and be instrumental in bringing this haunting vision to audiences worldwide.
            </Typography>

            <Typography variant="h5" component="h3" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#ff4d4d' }}>
              Connect With Us
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              If you share our passion for independent horror and wish to explore how a partnership can make a significant impact, we invite you to connect with our team. We can provide detailed information on the Executive Producer role and discuss current opportunities.
            </Typography>
            <Typography variant="h6" component="p" align="center" sx={{ mt: 4, mb: 2, fontWeight:'bold' }}>
              Contact us for partnership details:
            </Typography>
             <Box textAlign="center" sx={{mb: 4}}>
                <Button 
                    variant="contained"
                    href={`mailto:${contactEmail}?subject=Executive Producer Inquiry - Screamify`}
                    sx={{
                        bgcolor: '#ff0000',
                        backgroundImage: 'linear-gradient(45deg, #ff0000, #cc0000)',
                        color: '#ffffff',
                        py: 1.5,
                        px: 5,
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(255, 0, 0, 0.25)',
                        letterSpacing: '0.5px',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        '&:hover': {
                            bgcolor: '#cc0000',
                            backgroundImage: 'linear-gradient(45deg, #cc0000, #990000)',
                            boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)'
                        }
                    }}
                >
                    Contact Us: {contactEmail}
                </Button>
            </Box>
            
             <Typography variant="body2" align="center" sx={{ mt: 4, color: 'rgba(255, 255, 255, 0.7)' }}>
              Let's build the future of independent horror, together.
            </Typography>
          </Paper>
        </Container>
        <Footer />
      </Box>
    </HelmetProvider>
  );
}

export default Invest; 